import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { useStore } from "../../../helpers";
import VoterPhotoModal from "./voter-photo-modal";
import VoterPhotoTrigger from "./voter-photo-trigger";
import { useVoterPhotoFaceFlow } from "./use-voter-photo-face-flow";
import { readImageFileAsDataUrl } from "./voter-photo-file";
import { isFaceRetakeError, isFaceServerError } from "./face-search-errors";
import type { SrFaceDecision, VoterPhotoUploaded } from "./sr-face-check-types";

type Props = {
    voterId?: string | null;
    currentPhotoS3Path?: string | null;
    onUploaded: (result: VoterPhotoUploaded | string) => void;
};

const VoterPhotoCapture = ({ voterId, currentPhotoS3Path, onUploaded }: Props) => {
    const { projectStore } = useStore();
    const cameraRef = useRef<any>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const voterKey = String(voterId || "").trim().replace(/[^\w-]/g, "");

    const flow = useVoterPhotoFaceFlow({
        voterId: voterKey,
        projectId: projectStore.activeProject?.id,
        onUploaded,
    });

    const closeModal = () => {
        if (flow.isBusy) return;
        setIsOpen(false);
        setCapturedImage(null);
        flow.resetFlow();
    };

    const openCamera = () => {
        if (!voterKey) {
            toast.error("Please select a voter before capturing the photo.");
            return;
        }
        setCapturedImage(null);
        flow.resetFlow();
        setIsOpen(true);
    };

    const handleCapture = () => {
        try {
            const image = cameraRef.current?.takePhoto();
            if (image) setCapturedImage(image);
        } catch (error) {
            console.error("Unable to capture voter photo:", error);
            toast.error("Unable to capture photo.");
        }
    };

    const afterAsync = async (ok: Promise<boolean> | boolean) => {
        if (await ok) closeModal();
    };

    const handleRetake = () => {
        setCapturedImage(null);
        flow.resetFlow();
    };

    const modalTitle = flow.isBusy && !flow.faceCheck && !flow.faceError
        ? "Checking face"
        : flow.faceError
          ? isFaceServerError(flow.faceError)
              ? "Something went wrong"
              : isFaceRetakeError(flow.faceError)
                ? flow.faceError.code === "MULTIPLE_FACES"
                    ? "Multiple faces detected"
                    : "No face detected"
                : "Unable to check face"
          : flow.faceCheck
            ? "Confirm face match"
            : "Capture Voter Photo";

    const modalSubtitle = flow.isBusy && !flow.faceCheck && !flow.faceError
        ? "Please wait while we compare this photo."
        : flow.faceError
          ? isFaceRetakeError(flow.faceError)
              ? "Please capture a clearer photo of a single face."
              : "Please try again later or retake the photo."
          : flow.faceCheck
            ? "Select a match, then Replace or Keep once."
            : `Photo will be saved as ${voterKey}.png`;

    return (
        <>
            <VoterPhotoTrigger hasPhoto={Boolean(currentPhotoS3Path)} onClick={openCamera} />
            {isOpen && (
                <VoterPhotoModal
                    title={modalTitle}
                    subtitle={modalSubtitle}
                    isBusy={flow.isBusy}
                    capturedImage={capturedImage}
                    faceCheck={flow.faceCheck}
                    faceError={flow.faceError}
                    cameraRef={cameraRef}
                    fileInputRef={fileInputRef}
                    onClose={closeModal}
                    onCapture={handleCapture}
                    onFileChange={(e) => {
                        readImageFileAsDataUrl(e.target.files?.[0], setCapturedImage);
                        e.target.value = "";
                    }}
                    onRetake={handleRetake}
                    onConfirm={() => {
                        if (!capturedImage) {
                            toast.error("Please capture or select a photo first.");
                            return;
                        }
                        afterAsync(flow.uploadAndCheck(capturedImage));
                    }}
                    onDecide={(id: string, decision: SrFaceDecision) =>
                        afterAsync(flow.applyDecision(id, decision))
                    }
                    onSkipNoMatch={() => afterAsync(flow.continueWithoutMatch())}
                />
            )}
        </>
    );
};

export default VoterPhotoCapture;
